import React from "react";
import { Link } from "react-router-dom";
import { Nav } from "@/components/layout/Nav";
import { Footer } from "@/components/layout/Footer";
import { PageSEO } from "@/components/seo/PageSEO";
import { SITE_URL } from "@/utils/seo";
import { CheckCircle, ArrowRight, Shield, Star } from "lucide-react";

const SITE = SITE_URL;

const cribs = [
  { name: "Full-Size Portable Crib", desc: "Spacious, safe, and set up with fresh laundered linens. Fits DVC studios and standard Disney resort rooms.", tag: "Most popular" },
  { name: "Pack 'n Play with Bassinet", desc: "Travel-ready bassinet + play yard. Ideal for younger infants who need a higher sleep surface.", tag: "Best for infants" },
  { name: "Pack 'n Play Standard", desc: "Durable play yard that doubles as an overnight sleeper. Low profile fits in tight vacation home spaces.", tag: "" },
];

const faqs = [
  {
    q: "Do you deliver cribs to Disney World resorts?",
    a: "Yes. We deliver full-size cribs and pack 'n plays to every Walt Disney World resort. Delivery and pickup require an in-person lobby meeting. We do not leave cribs with bell services.",
  },
  {
    q: "Will a full-size crib fit in a DVC studio at Disney World?",
    a: "Yes. Our full-size cribs are designed to fit in standard Disney resort rooms and DVC studio layouts. We have delivered cribs to every DVC property and know which rooms have the most space.",
  },
  {
    q: "Do the cribs come with linens?",
    a: "Yes. Every crib and pack 'n play includes freshly laundered linens — fitted sheet and sleep-safe crib sheet — washed and prepared before each rental.",
  },
  {
    q: "Are your cribs safe and certified?",
    a: "All of our cribs and pack 'n plays meet current CPSC safety standards. We inspect and sanitize every unit between rentals and replace any item that shows wear.",
  },
  {
    q: "Can I rent a crib for just one night near Disney?",
    a: "Yes. We have no minimum rental period beyond your chosen dates. You can rent a crib for a single night or for the full length of your Disney trip.",
  },
  {
    q: "How do I set up the crib once it arrives?",
    a: "We set up the crib for you at delivery. You do not need to assemble anything — we bring it fully assembled to your room (Disney resorts) or to the front of your vacation home.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    { "@type": "ListItem", position: 2, name: "Baby Gear", item: `${SITE}/gear` },
    { "@type": "ListItem", position: 3, name: "Crib Rental Disney World", item: `${SITE}/crib-rental-disney-world` },
  ],
};

export default function CribRentalDisney() {
  return (
    <>
      <PageSEO
        title="Crib Rental Near Disney World | Pack n Play & Baby Crib Rental Orlando"
        description="Rent a full-size crib or pack 'n play for your Disney World vacation. Fresh linens included. Free delivery to every Disney resort, Universal Orlando and Orlando vacation homes. Enchanted Gear Rentals."
        canonical={`${SITE}/crib-rental-disney-world`}
        structuredData={[breadcrumbSchema, faqSchema]}
      />
      <Nav />
      <main className="min-h-screen" style={{ background: "#040A0F" }}>

        {/* Hero */}
        <section className="pt-28 pb-16 text-center" style={{ background: "linear-gradient(180deg, #060f18 0%, #040A0F 100%)" }}>
          <div className="container max-w-3xl">
            <nav aria-label="Breadcrumb" className="text-xs text-[#B9C8D5] mb-4">
              <Link to="/" className="hover:text-[#FFD166]">Home</Link>
              <span className="mx-2 opacity-40">/</span>
              <Link to="/gear" className="hover:text-[#FFD166]">Baby Gear</Link>
              <span className="mx-2 opacity-40">/</span>
              <span className="text-[#FFD166]">Crib Rental Disney World</span>
            </nav>
            <h1 className="font-serif text-4xl md:text-5xl text-[#F6F1E4] mb-5">
              Crib Rental Near Disney World
            </h1>
            <p className="text-[#B9C8D5] text-lg max-w-2xl mx-auto">
              Full-size cribs and pack 'n plays delivered to your Disney resort, vacation home or Orlando hotel — with fresh laundered linens on every rental. No assembly required. We set it up for you.
            </p>
            <div className="mt-7 flex gap-4 justify-center flex-wrap">
              <Link to="/#trip-setup"><button className="btn-primary" data-testid="crib-cta-top">Check Availability <ArrowRight className="inline w-4 h-4 ml-1" /></button></Link>
              <Link to="/gear"><button className="btn-ghost">Browse All Baby Gear</button></Link>
            </div>
          </div>
        </section>

        {/* Crib types */}
        <section className="container py-14">
          <h2 className="font-serif text-3xl text-[#F6F1E4] mb-3">Cribs available for Disney World rentals</h2>
          <p className="text-[#B9C8D5] mb-8 max-w-2xl">Every crib rental includes fresh linens, in-room setup and professional inspection before delivery.</p>
          <div className="grid md:grid-cols-3 gap-6">
            {cribs.map((c) => (
              <div key={c.name} className="rounded-xl bg-[#0A1825] border border-[#FFD166]/15 p-6 relative">
                {c.tag && <span className="absolute top-4 right-4 text-xs bg-[#FFD166]/15 text-[#FFD166] px-2 py-0.5 rounded-full">{c.tag}</span>}
                <h3 className="font-semibold text-[#F6F1E4] mb-2 pr-20">{c.name}</h3>
                <p className="text-sm text-[#B9C8D5]">{c.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Why choose us */}
        <section className="container py-10">
          <h2 className="font-serif text-3xl text-[#F6F1E4] mb-8">Why Disney families choose Enchanted for crib rentals</h2>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl">
            {[
              { icon: CheckCircle, title: "Fresh linens every rental", body: "Every crib arrives with freshly laundered sheets — no bare mattresses. We treat every rental like we would for our own children." },
              { icon: Shield, title: "CPSC-certified safe sleep", body: "All cribs and pack 'n plays meet current Consumer Product Safety Commission standards. We inspect every unit before and after each rental." },
              { icon: CheckCircle, title: "We set it up for you", body: "We bring the crib fully assembled to your Disney resort room or vacation home. You focus on the magic — we handle the setup." },
              { icon: Star, title: "We know Disney resort rooms", body: "We have delivered cribs to every DVC property and Disney resort. We know which rooms have the most floor space and how to configure for the best fit." },
            ].map(({ icon: Icon, title, body }) => (
              <div key={title} className="flex gap-4 rounded-xl bg-[#0A1825] border border-[#FFD166]/10 p-5">
                <Icon className="w-5 h-5 text-[#FFD166] shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-[#F6F1E4]">{title}</div>
                  <div className="text-sm text-[#B9C8D5] mt-1">{body}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Delivery info */}
        <section className="container py-10">
          <div className="max-w-2xl rounded-2xl bg-[#0A1825] border border-[#FFD166]/20 p-7">
            <h2 className="font-serif text-2xl text-[#F6F1E4] mb-4">Crib delivery to Disney World resorts</h2>
            <p className="text-[#B9C8D5] text-sm mb-4">
              We deliver to every Walt Disney World resort property — from Grand Floridian and Polynesian to All-Star Movies and Port Orleans. Delivery and pickup require an in-person lobby meeting. Disney does not allow third-party vendors to leave items at bell services.
            </p>
            <ul className="space-y-2 text-sm text-[#B9C8D5]">
              <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />Standard delivery is free to all Disney resort zones</li>
              <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />Also available to Universal, vacation homes, Kissimmee and area hotels</li>
              <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />1-hour delivery windows from 7 am to 10 pm</li>
              <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />Delivery is always free</li>
            </ul>
          </div>
        </section>

        {/* FAQ */}
        <section className="container py-10">
          <h2 className="font-serif text-3xl text-[#F6F1E4] mb-8">Crib rental FAQ — Disney World &amp; Orlando</h2>
          <div className="space-y-4 max-w-3xl">
            {faqs.map((f) => (
              <details key={f.q} className="group rounded-xl bg-[#0A1825] border border-[#FFD166]/10 p-5">
                <summary className="font-semibold text-[#F6F1E4] cursor-pointer list-none flex justify-between items-center gap-3">
                  {f.q}
                  <span className="text-[#FFD166] text-xl leading-none shrink-0 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-[#B9C8D5] text-sm leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Related */}
        <section className="container py-8">
          <h2 className="font-serif text-2xl text-[#F6F1E4] mb-5">Also renting for your Disney trip?</h2>
          <div className="flex flex-wrap gap-3">
            {[
              { label: "Stroller rentals Disney World", to: "/disney-world-stroller-rentals" },
              { label: "Baby gear rental near Disney", to: "/baby-gear-rental-disney-world" },
              { label: "Baby Brezza rental Orlando", to: "/baby-brezza-rental-orlando" },
              { label: "Double stroller rentals", to: "/double-stroller-rentals-orlando" },
              { label: "Last-minute stroller rental", to: "/last-minute-stroller-rental-disney-world" },
            ].map((l) => (
              <Link key={l.to} to={l.to} className="text-sm text-[#FFD166] border border-[#FFD166]/25 px-4 py-2 rounded-full hover:bg-[#FFD166]/10 transition-colors">{l.label}</Link>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="container pb-20 text-center">
          <div className="max-w-xl mx-auto rounded-2xl bg-[#0A1825] border border-[#FFD166]/20 p-8">
            <h2 className="font-serif text-2xl text-[#F6F1E4] mb-3">Reserve your crib for Disney World</h2>
            <p className="text-[#B9C8D5] mb-6">Enter your dates and resort to confirm availability and secure your crib rental in minutes.</p>
            <Link to="/#trip-setup"><button className="btn-primary" data-testid="crib-cta-bottom">Check Availability <ArrowRight className="inline w-4 h-4 ml-1" /></button></Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
